import {useEffect, useState, type FormEvent} from 'react'
import {useParams} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {Skeleton} from '@/components/ui/skeleton'
import {Hint} from '@/components/ui/hint'
import {HelpIcon} from '@/components/help-icon'
import {PorQue} from '@/components/ui/por-que'
import {Tag} from '@/components/ui/tag'
import {FieldError} from '@/components/field-error'
import {Fact as Stamp, TagList} from '@/components/fact'
import {KairosMark} from '@/components/kairos-mark'
import {PageHeading} from '@/components/page-heading'
import {SectionCard} from '@/components/section-card'
import {LicenciaSstDetail} from '@/components/licencia-sst-section'
import {useBusiness} from '@/hooks/empresa/organizacion/use-businesses'
import {useMyEmployee, useUpdateMyEmployee, type Employee} from '@/hooks/empresa/identidad/use-employees'
import {useGodModeReadOnly} from '@/hooks/platform/use-god-mode'
import {useMyLicencia} from '@/hooks/inci/consola/use-licencia'
import {translateApiError} from '@/lib/errors'
import {validateEmail, validatePhone} from '@/lib/validation'
import {tope} from '@/lib/campos-texto'

type IdentityErrors = {name?: string; lastName?: string; phone?: string; email?: string}

function MiPerfilForm({businessId, employee}: {businessId: string; employee: Employee}) {
  const {t} = useTranslation()
  const godReadOnly = useGodModeReadOnly()
  const update = useUpdateMyEmployee(businessId)
  const [name, setName] = useState(employee.name)
  const [lastName, setLastName] = useState(employee.last_name)
  const [phone, setPhone] = useState(employee.phone ?? '')
  const [email, setEmail] = useState(employee.email ?? '')
  const [errors, setErrors] = useState<IdentityErrors>({})
  const [formError, setFormError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    setName(employee.name)
    setLastName(employee.last_name)
    setPhone(employee.phone ?? '')
    setEmail(employee.email ?? '')
  }, [employee.name, employee.last_name, employee.phone, employee.email])

  function blurField(field: keyof IdentityErrors) {
    setErrors((prev) => {
      const next = {...prev}
      if (field === 'name') next.name = name.trim() ? undefined : 'errors.nameRequired'
      if (field === 'lastName') next.lastName = lastName.trim() ? undefined : 'errors.nameRequired'
      if (field === 'phone') {
        next.phone = phone && phone !== (employee.phone ?? '') ? (validatePhone(phone) ?? undefined) : undefined
      }
      if (field === 'email') {
        next.email = email && email !== (employee.email ?? '') ? (validateEmail(email) ?? undefined) : undefined
      }
      return next
    })
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (godReadOnly) return
    setSaved(false)
    setFormError(null)
    // Values are i18n keys — FieldError translates them.
    const next: IdentityErrors = {}
    if (!name.trim()) next.name = 'errors.nameRequired'
    if (!lastName.trim()) next.lastName = 'errors.nameRequired'
    if (phone && phone !== (employee.phone ?? '')) next.phone = validatePhone(phone) ?? undefined
    if (email && email !== (employee.email ?? '')) next.email = validateEmail(email) ?? undefined
    setErrors(next)
    if (Object.values(next).some(Boolean)) return
    update.mutate(
      {name: name.trim(), last_name: lastName.trim(), phone: phone || null, email: email || null},
      {
        onSuccess: () => setSaved(true),
        onError: (err) => setFormError(translateApiError(err)),
      }
    )
  }

  return (
    <form onSubmit={handleSubmit} noValidate data-testid="nav-datos">
      <div className="flex flex-col gap-4">
        <div className="space-y-2">
          <Label htmlFor="mi-perfil-name">{t('perfil.name')}</Label>
          <Input
            id="mi-perfil-name"
            value={name}
            maxLength={tope('NOM')}
            disabled={godReadOnly}
            onChange={(e) => {
              setName(e.target.value)
              setErrors((f) => ({...f, name: undefined}))
              setSaved(false)
            }}
            onBlur={() => blurField('name')}
            aria-invalid={!!errors.name}
          />
          <FieldError error={errors.name} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="mi-perfil-last-name">{t('perfil.lastName')}</Label>
          <Input
            id="mi-perfil-last-name"
            value={lastName}
            maxLength={tope('NOM')}
            disabled={godReadOnly}
            onChange={(e) => {
              setLastName(e.target.value)
              setErrors((f) => ({...f, lastName: undefined}))
              setSaved(false)
            }}
            onBlur={() => blurField('lastName')}
            aria-invalid={!!errors.lastName}
          />
          <FieldError error={errors.lastName} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="mi-perfil-phone">{t('perfil.phone')}</Label>
          <Input
            id="mi-perfil-phone"
            inputMode="tel"
            value={phone}
            maxLength={tope('TEL')}
            disabled={godReadOnly}
            onChange={(e) => {
              setPhone(e.target.value)
              setErrors((f) => ({...f, phone: undefined}))
              setSaved(false)
            }}
            onBlur={() => blurField('phone')}
            aria-invalid={!!errors.phone}
          />
          <FieldError error={errors.phone} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="mi-perfil-email">{t('perfil.email')}</Label>
          <Input
            id="mi-perfil-email"
            inputMode="email"
            autoComplete="email"
            value={email}
            maxLength={tope('MAIL')}
            disabled={godReadOnly}
            onChange={(e) => {
              setEmail(e.target.value)
              setErrors((f) => ({...f, email: undefined}))
              setSaved(false)
            }}
            onBlur={() => blurField('email')}
            aria-invalid={!!errors.email}
          />
          <FieldError error={errors.email} />
        </div>
      </div>
      {!email && (
        <p className="notice-amber mt-3 rounded-lg px-3 py-2 text-xs" data-testid="mi-perfil-email-prompt">
          {t('perfil.emailPrompt')}
        </p>
      )}
      {formError && <p className="mt-3 text-sm text-destructive">{formError}</p>}
      <div className="mt-4 flex items-center gap-3">
        <Button type="submit" size="sm" disabled={update.isPending || godReadOnly}>
          {t('perfil.save')}
        </Button>
        {saved && <span className="text-sm text-primary">{t('perfil.saved')}</span>}
      </div>
    </form>
  )
}

function LicenciaSstSection({role}: {role: Employee['role']}) {
  const {t} = useTranslation()
  const {data: licencia, isLoading} = useMyLicencia()
  // Colapsada y no oculta para un `user` sin licencia: subirla es la vía para volverse responsable.
  const [revealed, setRevealed] = useState(false)

  if (isLoading) return <Skeleton className="h-32 w-full" />
  if (!licencia) return null

  const hasLicencia = licencia.estado !== null
  const collapsed = role === 'user' && !hasLicencia && !revealed

  return (
    <SectionCard
      title={
        <span className="flex items-center gap-1.5">
          {t('licencia.sectionTitle')}
          {collapsed && (
            <HelpIcon label={t('licencia.sectionTitle')} testId="licencia-collapsed-tip">
              {t('licencia.collapsedHint')}
            </HelpIcon>
          )}
        </span>
      }
      testId="licencia-sst-section"
      bodyClassName={collapsed ? 'px-5 py-4' : undefined}>
      {collapsed ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setRevealed(true)}
          data-testid="licencia-reveal">
          {t('licencia.collapsedCta')}
        </Button>
      ) : (
        <LicenciaSstDetail licencia={licencia} />
      )}
    </SectionCard>
  )
}

function IdentityCard({employee}: {employee: Employee}) {
  const {t} = useTranslation()
  const initials = `${employee.name.charAt(0)}${employee.last_name.charAt(0)}`.toUpperCase()
  return (
    <aside className="surface xl:sticky xl:top-2">
      <div className="surface-divider flex items-start gap-4 border-b p-5">
        <span
          aria-hidden="true"
          className="flex size-12 shrink-0 items-center justify-center rounded-full bg-secondary font-heading text-base text-primary">
          {initials}
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="font-heading text-lg break-words text-foreground">
            {employee.name} {employee.last_name}
          </h2>
          <p className="mt-1.5 flex flex-wrap items-center gap-2">
            <span className="font-mono text-xs text-muted-foreground">{employee.cedula}</span>
            <Tag tone="neutral">{t(`employees.status.${employee.status}`)}</Tag>
          </p>
        </div>
      </div>
      <div className="space-y-4 p-5">
        <Stamp label={t('perfil.position')}>
          <TagList names={employee.positions.map((p) => p.name)} />
        </Stamp>
        <Stamp label={t('perfil.area')}>
          <TagList names={employee.areas.map((a) => a.name)} />
        </Stamp>
        <Stamp label={t('perfil.employmentType')}>
          <Tag tone="neutral">{t(`employees.employmentType.${employee.employment_type}`)}</Tag>
        </Stamp>
      </div>
      {/* `div` y no `p`: `Hint` ya renderiza su propio `<p>`. */}
      <div className="surface-divider flex items-center gap-1.5 border-t px-5 py-4">
        <Hint>{t('perfil.managedNote')}</Hint>
        <HelpIcon label={t('perfil.managedLabel')} testId="perfil-managed-tip">
          {t('perfil.managedTip')}
        </HelpIcon>
      </div>
    </aside>
  )
}

export function MiPerfilPage() {
  const {t} = useTranslation()
  const {businessId} = useParams()
  const {data: business} = useBusiness(businessId)
  const {data: employee, isLoading, isError} = useMyEmployee(businessId)

  if (isLoading) return <Skeleton className="h-60 w-full" />
  // Un miembro virtual de modo dios no tiene registro propio acá.
  if (isError || !employee)
    return (
      <div className="space-y-5" data-testid="mi-perfil">
        <PageHeading title={t('perfil.title')} subtitle={business?.name} />
        <div className="surface mx-auto max-w-2xl px-6 py-14 text-center">
          <div className="flex justify-center">
            <KairosMark />
          </div>
          <p className="mt-3 font-heading text-lg text-foreground">{t('perfil.noRecord')}</p>
          <div className="mt-2">
            <PorQue
              label={t('perfil.noRecordWhy')}
              testId="perfil-no-record-why"
              contenidoTestId="perfil-no-record-why-content">
              <p>{t('perfil.noRecordBody')}</p>
            </PorQue>
          </div>
        </div>
      </div>
    )

  return (
    <div className="space-y-5" data-testid="mi-perfil">
      <PageHeading title={t('perfil.title')} subtitle={business?.name} />

      <div className="grid gap-5 xl:grid-cols-3 xl:items-start">
        <IdentityCard employee={employee} />
        <div className="space-y-5 xl:col-span-2">
          <SectionCard title={t('perfil.contactSection')}>
            <MiPerfilForm businessId={businessId!} employee={employee} />
          </SectionCard>
          <LicenciaSstSection role={employee.role} />
        </div>
      </div>
    </div>
  )
}
